// layouts/AdminLayout.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Auth-Context/AuthContext";
import AdminTopbar from "../../components/AdminTopbar";
import { getDashboardData } from "../../api/services";
import {
  LayoutDashboard, Users, FileText,
  LogOut, ChevronLeft, ChevronRight, X,
} from "lucide-react";

export function dispatchAdminSidebarUpdate(payload = {}) {
  window.dispatchEvent(
    new CustomEvent("crm:admin:sidebar:update", { detail: payload })
  );
}

const NAV = [
  { id: "overview", label: "Overview",   icon: LayoutDashboard, path: "/admin"                                    },
  { id: "users",    label: "Users",      icon: Users,           path: "/admin/users", badgeKey: "users"           },
  { id: "logs",     label: "Audit Logs", icon: FileText,        path: "/admin/logs",  badgeKey: "logs", badgeRed: true },
];

export default function AdminLayout({ children, title, subtitle, actions = [], search }) {
  const [collapsed,      setCollapsed]      = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile,       setIsMobile]       = useState(false);
  // badgeData holds counts for the admin nav — keys match badgeKey in NAV
  const [badgeData,      setBadgeData]      = useState({ users: 0, logs: 0 });

  const navigate    = useNavigate();
  const location    = useLocation();
  const { logout }  = useAuth();
  const prevPathRef = useRef(location.pathname);

  // ── Responsive ──────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Close mobile menu on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMobileMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // ── Fetch admin badge counts ─────────────────────────────────────────────
  const fetchBadges = useCallback(async () => {
    try {
      const data = await getDashboardData();
      setBadgeData({
        users: data.totalUsers ?? data.totalAgents ?? 0,
        logs:  data.unreadLogs ?? data.auditLogs   ?? 0,
      });
    } catch (err) {
      console.error("[AdminSidebar] badge fetch failed:", err);
    }
  }, []);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  // Re-fetch on route change
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      fetchBadges();
    }
  }, [location.pathname, fetchBadges]);

  // ── INSTANT sync via custom event ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const { users, logs } = e.detail ?? {};
      setBadgeData((prev) => ({
        users: typeof users === "number" ? users : prev.users,
        logs:  typeof logs  === "number" ? logs  : prev.logs,
      }));
    };
    window.addEventListener("crm:admin:sidebar:update", handler);
    return () => window.removeEventListener("crm:admin:sidebar:update", handler);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  // ── Nav items ────────────────────────────────────────────────────────────
  const NavItems = ({ mobile = false }) => (
    <>
      {NAV.map((item) => {
        const Icon     = item.icon;
        const isActive =
          location.pathname === item.path ||
          (item.path !== "/admin" && location.pathname.startsWith(item.path));
        const badge = item.badgeKey ? badgeData[item.badgeKey] : null;

        return (
          <button
            key={item.id}
            onClick={() => { navigate(item.path); if (mobile) setMobileMenuOpen(false); }}
            title={collapsed && !mobile ? item.label : undefined}
            className="flex items-center gap-2.5 w-full rounded-lg px-2.5 h-9 transition-all duration-150 text-left"
            style={isActive ? { background: "#009DB9", color: "#fff" } : { color: "#737373" }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f5f5f5"; }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
          >
            <Icon size={16} strokeWidth={isActive ? 2.5 : 2} className="flex-shrink-0" />
            {(!collapsed || mobile) && (
              <span className="text-[13px] font-medium flex-1 truncate">{item.label}</span>
            )}
            {(!collapsed || mobile) && badge > 0 && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={
                  isActive
                    ? { background: "rgba(255,255,255,0.25)", color: "#fff" }
                    : item.badgeRed
                    ? { background: "#fee2e2", color: "#dc2626" }
                    : { background: "rgba(0,157,185,0.12)", color: "#009DB9" }
                }
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </>
  );

  // ── Shared sidebar body ──────────────────────────────────────────────────
  const SidebarInner = ({ mobile = false }) => (
    <>
      <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5 overflow-y-auto">
        {(!collapsed || mobile) && (
          <div className="px-3 pb-2 text-[9px] font-semibold uppercase tracking-[.1em] text-neutral-400">
            Management
          </div>
        )}
        <NavItems mobile={mobile} />
      </nav>
      <div className="px-2 py-3 border-t border-neutral-100">
        <button
          onClick={() => { handleLogout(); if (mobile) setMobileMenuOpen(false); }}
          className="flex items-center gap-2.5 w-full rounded-lg px-2.5 h-9 text-neutral-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          title={collapsed && !mobile ? "Logout" : undefined}
        >
          <LogOut size={16} strokeWidth={2} className="flex-shrink-0" />
          {(!collapsed || mobile) && <span className="text-[13px] font-medium">Logout</span>}
        </button>
      </div>
    </>
  );

  // ── Logo block ───────────────────────────────────────────────────────────
  const LogoBlock = ({ showText = true }) => (
    <div
      className="flex items-center flex-shrink-0 px-4 overflow-hidden border-b border-neutral-100"
      style={{ height: "56px" }}
    >
      <img
        src="/IZ-Logo.png"
        alt="IZ Sales"
        className="flex-shrink-0 object-contain w-auto h-7"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "block";
        }}
      />
      <span className="flex-shrink-0 text-xl font-bold tracking-tight" style={{ color: "#009DB9", display: "none" }}>IZ</span>
      {showText && (
        <div className="flex flex-col ml-2 overflow-hidden leading-none">
          <span className="text-[11px] font-bold text-neutral-800 whitespace-nowrap tracking-tight">IZSoftwares</span>
          <span className="text-[9px] text-neutral-400 whitespace-nowrap">Super Admin</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">

      {/* Desktop sidebar */}
      <aside
        className="relative flex-col flex-shrink-0 hidden transition-all duration-300 ease-in-out bg-white border-r md:flex border-neutral-100"
        style={{ width: isMobile ? 0 : collapsed ? "68px" : "200px", boxShadow: "1px 0 0 #f0f0f0" }}
      >
        <LogoBlock showText={!collapsed} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-[38px] z-20 w-6 h-6 rounded-full bg-white border border-neutral-200 flex items-center justify-center shadow-sm hover:bg-neutral-50 transition-colors"
        >
          {collapsed
            ? <ChevronRight size={12} className="text-neutral-500" />
            : <ChevronLeft  size={12} className="text-neutral-500" />}
        </button>
        <SidebarInner mobile={false} />
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)" }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <aside
        className="fixed top-0 left-0 z-50 flex flex-col h-full transition-transform duration-300 ease-in-out bg-white md:hidden"
        style={{
          width: "260px",
          boxShadow: "4px 0 24px rgba(0,0,0,0.12)",
          transform: mobileMenuOpen ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        <div className="flex items-center justify-between flex-shrink-0 px-4 border-b border-neutral-100" style={{ height: "56px" }}>
          <LogoBlock showText />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-center w-8 h-8 transition-colors rounded-lg text-neutral-400 hover:bg-neutral-100"
          >
            <X size={16} />
          </button>
        </div>
        <SidebarInner mobile={true} />
      </aside>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <AdminTopbar
          title={title}
          subtitle={subtitle}
          actions={actions}
          search={search}
          onMenuClick={() => setMobileMenuOpen(true)}
        />
        <main className="flex-1 px-4 py-4 overflow-y-auto md:px-7 md:py-6">
          {children}
        </main>
      </div>
    </div>
  );
}